import { PropsWithChildren, useMemo } from "react";

import { Box, Button, Flex, Grid, Icon, Typography } from "@aircall/tractor";
import { Duration, getRelativeDate } from "@dashboard/library";
import { differenceInSeconds } from "date-fns";
import { useTranslation } from "react-i18next";

import { SectionHeader } from "../SectionHeader";
import { SectionLayout } from "../SectionLayout";

import i18n from "@/config/i18n.config";
import { DASHBOARD_URL } from "@/constants/environment.constants";
import { longPrettifiedDate } from "@/helpers/date/date.helpers";
import { useCall } from "@/hooks/useCall/useCall";
import { ExternalOutlined } from "@aircall/icons";
import styled from "@xstyled/styled-components";
import { useDate } from "./SectionTiming.hooks";

export function buildCallTimelineLink(callId: string) {
  // Trailing slash inside DASHBOARD_URL.
  return `${DASHBOARD_URL}calls/${callId}/timeline`;
}

const StyledButtonWrapper = styled(Box)`
  > a {
    padding: 0;
  }
`;

function GridEntry({ label, children }: PropsWithChildren<{ label: string }>) {
  return (
    <Box display="contents">
      <Typography variant="bodyMediumS" color="text-secondary">
        {label}
      </Typography>
      <Box>{children}</Box>
    </Box>
  );
}

export function SectionTiming() {
  const { t } = useTranslation();
  const { call } = useCall();
  const { displayRelativeDay } = useDate({
    targetDate: new Date(call?.createdAt ?? ""),
  });

  const timing = useMemo(() => {
    if (call?.answeredAt && call?.durationInSecond) {
      const waitedTime = differenceInSeconds(
        new Date(call.answeredAt),
        new Date(call.createdAt)
      );

      const inCallTime = call.durationInSecond - waitedTime;

      return { waitedTime, inCallTime };
    }
  }, [call?.answeredAt, call?.createdAt, call?.durationInSecond]);

  if (!call) {
    return null;
  }

  return (
    <SectionLayout data-test="timing-section">
      <SectionHeader>{t("call_context.timing")}</SectionHeader>
      <Flex flexDirection="column" gap="xxs">
        <Grid gap="xxs" gridTemplateColumns="auto 1fr">
          <GridEntry label={t("call_context.start_time")}>
            <Flex alignItems="center" gap="xxxs">
              <Typography>
                {`${longPrettifiedDate(new Date(call.createdAt), { locale: i18n.language })}`}
              </Typography>
              {displayRelativeDay && (
                <Typography color="text-secondary">
                  {`(${getRelativeDate(new Date(call.createdAt), i18n.language)})`}
                </Typography>
              )}
            </Flex>
          </GridEntry>
          {call.answeredAt &&
            timing?.waitedTime &&
            call.type !== "voicemail" && (
              <GridEntry label={t("call_context.waited_time")}>
                <Duration seconds={timing.waitedTime} />
              </GridEntry>
            )}
          {call.durationInSecond &&
            timing?.inCallTime &&
            call.type !== "voicemail" && (
              <GridEntry label={t("call_context.in_call_time")}>
                <Duration seconds={timing.inCallTime} />
              </GridEntry>
            )}
          {call.durationInSecond &&
            call.answeredAt &&
            call.type !== "voicemail" && (
              <GridEntry label={t("call_context.total_call_time")}>
                <Duration seconds={call.durationInSecond} />
              </GridEntry>
            )}
        </Grid>
        {call.direction === "inbound" && (
          <StyledButtonWrapper>
            <Button
              data-test="open-call-timeline-anchor"
              as="a"
              target="_blank"
              href={buildCallTimelineLink(call.id)}
              mode="link"
              shape="default"
              size="regular"
            >
              <Flex alignItems="center" gap="xxxs">
                <Typography>{t("open_call_timeline")}</Typography>
                <Icon component={ExternalOutlined} size={18} />
              </Flex>
            </Button>
          </StyledButtonWrapper>
        )}
      </Flex>
    </SectionLayout>
  );
}
